import React from 'react';
import { motion } from 'framer-motion';
import { 
  Code, Database, Shield, Globe, Smartphone, Cpu, Server,
  FileCode, Hammer, Terminal, Github, Settings, Repeat, Wrench
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';

const Skills = () => {
  const { skillsData } = useAppContext();
  
  // Helper function to get appropriate icon for a language
  const getLanguageIcon = (language) => {
    switch(language.toLowerCase()) {
      case 'python':
        return <FileCode className="h-5 w-5 text-primary" />;
      case 'php':
        return <Code className="h-5 w-5 text-primary" />;
      case 'java':
        return <Cpu className="h-5 w-5 text-primary" />;
      case 'html/css':
        return <Globe className="h-5 w-5 text-primary" />;
      case 'sql':
        return <Database className="h-5 w-5 text-primary" />;
      case 'c#':
        return <Terminal className="h-5 w-5 text-primary" />;
      default:
        return <Code className="h-5 w-5 text-primary" />;
    }
  };
  
  // Helper function to get appropriate icon for a tool
  const getToolIcon = (tool) => {
    switch(tool.toLowerCase()) {
      case 'git':
        return <Github className="h-5 w-5 text-primary" />;
      case 'docker':
        return <Repeat className="h-5 w-5 text-primary" />;
      case 'google scripts':
        return <FileCode className="h-5 w-5 text-primary" />;
      case 'linux':
        return <Terminal className="h-5 w-5 text-primary" />;
      default:
        return <Wrench className="h-5 w-5 text-primary" />;
    }
  };
  
  // Helper function to get appropriate icon for a framework
  const getFrameworkIcon = (framework) => {
    switch(framework.toLowerCase()) {
      case 'react':
        return <Globe className="h-5 w-5 text-primary" />;
      case 'node.js':
        return <Server className="h-5 w-5 text-primary" />;
      case 'laravel':
        return <Code className="h-5 w-5 text-primary" />;
      case 'ionic':
        return <Smartphone className="h-5 w-5 text-primary" />;
      case 'pipy':
        return <FileCode className="h-5 w-5 text-primary" />;
      default:
        return <Settings className="h-5 w-5 text-primary" />;
    }
  };
  
  return (
    <section id="skills" className="py-20 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl font-bold mb-2 terminal-text">
            <span className="text-primary">{'>'}</span> Skills
          </h2>
          <div className="w-20 h-1 bg-primary mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-background/80 border border-border/50 rounded-lg p-6 shadow-lg hover:border-primary/30 transition-all duration-300 card-hover"
          >
            <h3 className="text-xl font-bold mb-6 terminal-text">
              <span className="text-primary">{'>'}</span> Languages
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              {skillsData.languages.map((language, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center space-x-3"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {getLanguageIcon(language)}
                  </div>
                  <div>
                    <h4 className="font-medium">{language}</h4>
                  </div>
                </motion.div>
              ))}
            </div>

            <h3 className="text-xl font-bold mt-8 mb-6 terminal-text">
              <span className="text-primary">{'>'}</span> Tools
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              {skillsData.tools.map((tool, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center space-x-3"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {getToolIcon(tool)}
                  </div>
                  <div>
                    <h4 className="font-medium">{tool}</h4>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-background/80 border border-border/50 rounded-lg p-6 shadow-lg hover:border-primary/30 transition-all duration-300 card-hover"
          >
            <h3 className="text-xl font-bold mb-6 terminal-text">
              <span className="text-primary">{'>'}</span> Frameworks & Libraries
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              {skillsData.frameworks.map((framework, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center space-x-3"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {getFrameworkIcon(framework)}
                  </div>
                  <div>
                    <h4 className="font-medium">{framework}</h4>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-8">
              <h3 className="text-xl font-bold mb-6 terminal-text">
                <span className="text-primary">{'>'}</span> Technical Areas
              </h3>
              
              <div className="space-y-6">
                {skillsData.technical.map((skill, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, width: 0 }}
                    whileInView={{ opacity: 1, width: "100%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">{skill.name}</span>
                      <span className="text-primary">{skill.level}%</span>
                    </div>
                    <div className="w-full bg-background/50 rounded-full h-2.5">
                      <motion.div 
                        className="bg-primary h-2.5 rounded-full"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                      ></motion.div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
